<template>
  <div data-test-id="create-kiosk-form">
    <Loader :is-processing="state.isLoading" />
    <hr class="kiosk-horizontal-line mb-2" />
    <div v-if="createMode">
      <div
        v-if="isSlpTabVisible"
        ref="tabListRef"
        class="kiosk-pass-tabs"
        role="tablist"
        :aria-label="$t('kiosk.create_pass_tabs')"
        @keydown="onTabKeydown"
      >
        <button
          id="kiosk-create-pass-tab-new-pass"
          type="button"
          role="tab"
          class="kiosk-pass-tabs__tab"
          :class="{
            'kiosk-pass-tabs__tab--active':
              state.activeCreateTab === createPassTabs.NEW_PASS,
            'kiosk-pass-tabs__tab--limit': isPassLimitReached
          }"
          :tabindex="state.activeCreateTab === createPassTabs.NEW_PASS ? 0 : -1"
          :aria-controls="'kiosk-create-pass-panel-new-pass'"
          :aria-selected="
            state.activeCreateTab === createPassTabs.NEW_PASS ? 'true' : 'false'
          "
          data-testid="kiosk-create-pass-tab-new-pass"
          @click="setCreatePassTab(createPassTabs.NEW_PASS)"
        >
          {{ $t("kiosk.new_pass") }}
        </button>
        <button
          id="kiosk-create-pass-tab-slp"
          type="button"
          role="tab"
          class="kiosk-pass-tabs__tab"
          :class="{
            'kiosk-pass-tabs__tab--active':
              state.activeCreateTab === createPassTabs.SLP
          }"
          :tabindex="state.activeCreateTab === createPassTabs.SLP ? 0 : -1"
          :aria-controls="'kiosk-create-pass-panel-slp'"
          :aria-selected="
            state.activeCreateTab === createPassTabs.SLP ? 'true' : 'false'
          "
          data-testid="kiosk-create-pass-tab-slp"
          @click="setCreatePassTab(createPassTabs.SLP)"
        >
          {{ $t("kiosk.student_late_pass") }}
        </button>
      </div>
      <div
        id="kiosk-create-pass-panel-new-pass"
        role="tabpanel"
        aria-labelledby="kiosk-create-pass-tab-new-pass"
        v-show="state.activeCreateTab === createPassTabs.NEW_PASS"
      >
        <template v-if="isPassLimitReached">
          <div class="v3kiosk-pass-limit d-flex flex-column align-items-center">
            <div
              class="v3kiosk-pass-limit-card"
              data-testid="kiosk-pass-limit-card"
            >
              <div class="v3kiosk-pass-limit-card__meta">
                {{ $t("kiosk.pass_limit") }}: {{ passLimitAvailable }}/{{
                  passLimitMax
                }}
              </div>
              <div
                v-if="passLimit.from_date && passLimit.to_date"
                class="v3kiosk-pass-limit-card__meta"
              >
                {{ $helpers.transformDate(passLimit.from_date) }} -
                {{ $helpers.transformDate(passLimit.to_date) }}
              </div>
            </div>
            <div
              class="v3kiosk-pass-limit-available"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="kiosk-pass-limit-message"
            >
              {{ $t("kiosk.no_passes_left") }}
            </div>
            <BaseButton
              class="w-50 d-flex align-items-center justify-content-center"
              rounded
              @click="cancel()"
            >
              {{ $t("kiosk.close_button") }}
            </BaseButton>
          </div>
        </template>
        <template v-else>
          <div class="v3kiosk-create-pass-fields">
            <div class="kiosk-pass">
              <div class="mb-2">
                <div v-if="activeKiosk" class="kiosk-pass-from text-start m-0">
                  {{ $t("kiosk.departing_from") }}:
                  <span class="bolder">{{
                    activeKiosk.room
                      ? activeKiosk.room.name
                      : activeKiosk.user
                        ? activeKiosk.user.name
                        : ""
                  }}</span>
                </div>
              </div>
            </div>
            <div class="mb-2">
              <LazyLoadSelect
                data-test-id="kiosk-pass-destination"
                :accessible-label="$t('kiosk.destination')"
                :context="
                  activeKiosk && activeKiosk.school
                    ? activeKiosk.school.context
                    : null
                "
                :invalid-feedback="errors.to ? $t(errors.to) : ''"
                :is-multiple="false"
                :selected="state.form.to"
                type="kiosk"
                :adult-pass-request-disabled="true"
                :is-in-kiosk="true"
                :refresh-on-open="true"
                :placeholder="$t('common.select_one')"
                @changed="
                  (value) => {
                    checkUnavailable(value)
                  }
                "
              >
              </LazyLoadSelect>
            </div>

            <InfoBox v-if="destinationUnavailability" class="mb-2 danger"
              >{{
                destinationUnavailability.unavailable_type ==
                "App\\Models\\Room"
                  ? $t("kiosk.this_room_is_unavailable_until")
                  : $t("kiosk.this_adult_is_unavailable_until")
              }}
              <span
                class="mx-1"
                v-html="
                  $helpers.getHTMLDateTime(
                    destinationUnavailability.to_date,
                    'MMMM D, YYYY',
                    'h:mm A'
                  ) + '.'
                "
              />
              <span v-if="destinationUnavailability.comment"
                >{{ $t("kiosk.note") }}
                {{ destinationUnavailability.comment }}</span
              ></InfoBox
            >

            <div
              v-if="favorites && favorites.length"
              class="kiosk-favorites-text text-start"
            >
              {{ $t("kiosk.or_quick_select_from") }}
              <span class="kiosk-favorites-text-bolder">{{
                $t("kiosk.favorites")
              }}</span>
            </div>

            <FavoriteCarousel
              v-if="favorites && favorites.length"
              :teacher="true"
              :key="'destination'"
              :is-editable="false"
              :not-managable="true"
              :action="
                (elem) => {
                  state.form.to = elem
                  getFavorites()
                }
              "
              class="mb-3"
              section="destination"
              :selected="state.form.to"
              :options="favorites"
              :is-global="true"
            />

            <div
              v-if="
                state.form &&
                state.form.to &&
                state.form.to.value &&
                state.form.to.value.comment_type != 'Hidden'
              "
              class="mb-3 text-start"
            >
              <Label class="d-block text-start">
                {{ $t("kiosk.reason") }}
              </Label>
              <InputField
                data-test-id="kiosk-pass-comment"
                v-model="state.form.reason"
                :invalid-feedback="errors.reason ? $t(errors.reason) : ''"
                max-chars="250"
                :rows="4"
                class="w-full"
                type="textarea"
                :placeholder="
                  state.form.to.value.comment_type == 'Optional'
                    ? $t('kiosk.reason_for_pass_optional')
                    : $t('kiosk.reason_for_pass_required')
                "
              />
            </div>
            <div v-if="isTardyCreateAllow.ltc || isTardyCreateAllow.lts">
              <TardyRadioGroup
                :is-allowed-lates="true"
                :allowed-lates="isTardyCreateAllow"
                :auto-select-single-allowed-option="false"
                v-model="state.form.tardy_badge"
                class="mb-3"
              />
            </div>
          </div>
          <div class="d-flex justify-content-center gap-3">
            <BaseButton
              class="w-50 d-flex align-items-center justify-content-center"
              rounded
              @click="cancel()"
            >
              {{ $t("kiosk.cancel") }}
            </BaseButton>
            <BaseButton
              data-test-id="create-kiosk-pass-form-submit"
              class="w-50 d-flex align-items-center justify-content-center"
              rounded
              solid
              @click="createPass()"
            >
              {{ $t("kiosk.submit") }}
            </BaseButton>
          </div>
          <div v-if="state.reqResponse.message">
            <InfoBox
              class="my-4"
              :class="{ danger: state.reqResponse.type === 'danger' }"
              :announce-assertive="!!state.reqResponse.message"
              :message="state.reqResponse.message"
            />
          </div>
        </template>
      </div>
      <div
        v-if="isSlpTabVisible && state.activeCreateTab === createPassTabs.SLP"
        id="kiosk-create-pass-panel-slp"
        role="tabpanel"
        aria-labelledby="kiosk-create-pass-tab-slp"
      >
        <CreateKioskSlpPassForm
          @tab-changed="onSlpTabChanged"
          @kiosk-slp-created="onSlpCreated"
        />
      </div>
    </div>
    <div
      v-else
      :class="{ 'opacity-0': !activePass && !state.reqResponse.message }"
      class="px-3 py-5"
    >
      <div class="pass-form-red m-0">
        <span class="flaticon-warning-1" />
      </div>
      <h2
        v-if="activePass"
        role="status"
        aria-live="polite"
        class="kiosk-status-heading"
      >
        {{ $t("kiosk.the_student_already_has_an_active_pass") }}
      </h2>
      <h2
        data-test-id="create-kiosk-info-box"
        v-if="state.reqResponse.message"
        role="status"
        aria-live="polite"
        class="kiosk-status-heading"
      >
        {{ state.reqResponse.message }}
      </h2>
      <div v-if="state.reqResponse.errors" class="text-red">
        <div v-for="(err, index) in state.reqResponse.errors" :key="index">
          {{ err[0] }}
        </div>
      </div>
      <p v-if="state.timeout > 0" class="mb-0 mt-3">
        {{ $t("kiosk.this_screen_will_be_closed_in") }} {{ state.timeout }}
        {{ $t("kiosk.seconds") }}.
      </p>
      <p class="mt-0 px-1">
        <button type="button" class="kiosk-close-msg-btn" @click="cancel()">
          {{ $t("kiosk.or_click_here_to_close_it_now") }}
        </button>
      </p>
    </div>
  </div>
</template>

<script>
import FavoriteCarousel from "@/v3components/FavoriteCarousel.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelect.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import { useStore } from "vuex"
import {
  onMounted,
  computed,
  reactive,
  onUnmounted,
  watch,
  ref,
  nextTick
} from "vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import TardyRadioGroup from "@/v3components/Forms/TardyRadioGroup.vue"
import CreateKioskSlpPassForm from "@/v3components/Forms/KioskForms/CreateKioskSlpPassForm.vue"
import { KIOSK_CREATE_PASS_TABS } from "@/constants/kioskConstants"
import { useI18n } from "vue-i18n"

export default {
  name: "CreatePassForm",
  components: {
    FavoriteCarousel,
    LazyLoadSelect,
    Loader,
    Label,
    InputField,
    BaseButton,
    TardyRadioGroup,
    InfoBox,
    CreateKioskSlpPassForm
  },
  emits: ["kioskPassCreated", "kioskSlpCreated", "tabChanged"],
  setup(args, { emit }) {
    const store = useStore()
    const { t } = useI18n()

    const createPassTabs = KIOSK_CREATE_PASS_TABS
    const createPassTabOrder = [createPassTabs.NEW_PASS, createPassTabs.SLP]
    const tabListRef = ref(null)

    const state = reactive({
      interval: null,
      successTimeout: null,
      timeout: 60,
      isLoading: false,
      favoritesCollection: null,
      activeCreateTab: createPassTabs.NEW_PASS,
      form: {
        to: [],
        reason: "",
        tardy_badge: ""
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      }
    })

    const activeKiosk = computed(() => store.getters["kiosks/activeKiosk"])
    const access_token = computed(() => store.getters["kiosks/accessToken"])
    const activePass = computed(() => store.getters["kiosks/activePass"])
    const createNewPassData = computed(
      () => store.getters["kiosks/createNewPassData"]
    )

    const isTardyCreateAllow = computed(() => {
      return (
        activeKiosk.value?.allowed_late_passes || { ltc: false, lts: false }
      )
    })

    const isSlpTabVisible = computed(() => {
      return !!(isTardyCreateAllow.value?.ltc || isTardyCreateAllow.value?.lts)
    })

    const passLimit = computed(() => store.getters["passes/pass_limit"] || {})

    const isPassLimitReached = computed(() => {
      const limit = passLimit.value
      if (!limit) return false
      // Authoritative backend flag: it is set to true whenever the student is
      // out of passes, including a configured cap of 0 (which arrives as
      // has: false, indistinguishable from "no limit" by `has` alone). This is
      // checked BEFORE the `has` gate so a 0 cap still disables New Pass.
      if (limit.student_reached_limit) return true
      // `has` only gates the numeric fallback: when no limit is configured the
      // backend sends has: false (often max: 0) and New Pass must stay enabled.
      if (!limit.has) return false
      const max = Number(limit.max)
      // -1 (unlimited) or non-numeric max means no cap applies.
      if (Number.isNaN(max) || max === -1) return false
      // Usage has reached the cap.
      const current = Number(limit.current) || 0
      return current >= max
    })

    const passLimitMax = computed(() => {
      const max = Number(passLimit.value?.max)
      return max === -1 ? "∞" : max
    })

    const passLimitAvailable = computed(() => {
      const max = Number(passLimit.value?.max)
      if (max === -1) return "∞"
      const current = Number(passLimit.value?.current) || 0
      return max > current ? max - current : 0
    })

    const validationMessages = {
      required: "kiosk.this_field_is_required",
      notSameAsFrom: "kiosk.please_select_different_teacher_location"
    }

    const validations = {
      form: {
        from: {},
        to: {
          required: helpers.withMessage(validationMessages.required, required),
          notSameAsFrom: helpers.withMessage(
            validationMessages.notSameAsFrom,
            (value) => {
              if (value)
                return !(
                  state.form.from.id == state.form.to.value.id &&
                  state.form.from.type == state.form.to.value.type
                )
            }
          ),
          available: (value) => {
            if (value) {
              return !destinationUnavailability.value
            }
          }
        },
        reason: {
          required: helpers.withMessage(
            validationMessages.required,
            requiredIf(() => {
              if (state.form.to && state.form.to.value) {
                return state.form.to.value.comment_type == "Optional"
                  ? false
                  : state.form.to.value.comment_type == "Hidden"
                    ? false
                    : true
              }
            })
          )
        }
      }
    }

    const v$ = useVuelidate(validations.form, state.form)

    const isValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const createMode = computed(
      () => !activePass.value && !createNewPassData.value
    )

    const fromObj = computed(() => {
      return {
        type: activeKiosk.value.room_id
          ? "App\\Models\\Room"
          : "App\\Models\\User",
        id: activeKiosk.value.room_id
          ? activeKiosk.value.room_id
          : activeKiosk.value.user_id
            ? activeKiosk.value.user_id
            : ""
      }
    })

    const favorites = computed(() => {
      return state.favoritesCollection || activeKiosk.value.favorites
    })

    const destinationUnavailability = computed(() => {
      return state.form.to && state.form.to.value
        ? state.form.to.value.unavailability
        : null
    })

    onMounted(() => {
      if (!createMode.value) {
        /* Do not auto-dismiss while blocking on an active pass — user must close explicitly (WCAG 2.2). */
        if (activePass.value) {
          state.timeout = 0
        } else {
          state.interval = setInterval(() => {
            if (state.timeout > 0) {
              state.timeout--
            } else {
              clearInterval(state.interval)
              if (typeof cancel === "function") {
                cancel()
              }
            }
          }, 1000)
        }
      }
      state.form.from = activeKiosk.value.room_id
        ? { id: activeKiosk.value.room_id, type: "App\\Models\\Room" }
        : activeKiosk.value.user_id
          ? { id: activeKiosk.value.user_id, type: "App\\Models\\User" }
          : ""
    })

    onUnmounted(() => {
      clearInterval(state.interval)
      clearTimeout(state.successTimeout)
    })

    const createPass = (data) => {
      // A create already succeeded and navigation to the active-pass screen is
      // pending (successTimeout). Ignore further submits during that window so
      // a second pass can't be created before the first result is applied.
      if (state.successTimeout) {
        return
      }
      resetResponseMessages()
      if (isPassLimitReached.value) {
        return
      }
      if (isValid.value || data) {
        state.isLoading = true
        const pass = data
          ? data
          : {
              from_type: fromObj.value.type,
              from_id: fromObj.value.id,
              to_id: state.form.to.value.id,
              to_type: state.form.to.value.type,
              comment: state.form.reason,
              kiosk_id: activeKiosk.value.id,
              kiosk_passcode: activeKiosk.value.passcode,
              tardy_badge: state.form.tardy_badge
            }
        if (access_token.value) {
          store
            .dispatch("kiosks/createKioskPass", pass)
            .then((response) => {
              const data = response.data
              state.isLoading = false
              setSuccessResponse(data.message)
              // Track the timer so Cancel/logout during the wait can clear it,
              // otherwise it would reopen the active-pass screen after the
              // student has already been sent back to login.
              state.successTimeout = setTimeout(() => {
                state.successTimeout = null
                resetResponseMessages()
                emit("kioskPassCreated")
                store.commit("kiosks/SET_CREATE_NEW_PASS_DATA", null)
                if (data?.queue_pass) {
                  store.commit("kiosks/REPLACE_ACTIVE_PASS", data?.queue_pass)
                  store.commit("passes/SET_PASS_LIMIT", {})
                } else {
                  store.commit("kiosks/REPLACE_ACTIVE_PASS", data.pass)
                  store.commit("passes/SET_PASS_LIMIT", data.pass_limit)
                }
                getPassHistories()
              }, 2000)
            })
            .catch((err) => {
              if (err && err.response) {
                const response = err.response.data
                setErrorResponse(response.message, response.errors)
              }
              state.isLoading = false
            })
        } else {
          store.commit("kiosks/SET_CREATE_NEW_PASS_DATA", pass)
          emit("tabChanged", "userLogin")
        }
      } else {
        v$.value.$touch()
      }
    }

    const getPassHistories = () => {
      store.dispatch("kiosks/getPassHistories").then((res) => {
        const data = res?.data
        if (data && data.data) {
          store.commit("kiosks/SET_PASS_HISTORIES", data.data)
        }
      })
    }

    const getFavorites = () => {
      store
        .dispatch("kiosks/getFavorites", activeKiosk.value.school.context)
        .then((response) => {
          if (response.data.data && response.data.data.length) {
            state.favoritesCollection = response.data.data
          }
        })
    }

    const setSuccessResponse = (message) => {
      state.reqResponse = {
        type: "success",
        message: message ? message : t("common.successfully_created"),
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : t("kiosk.something_went_wrong"),
        errors: errors ? errors : {}
      }
    }
    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const cancel = () => {
      // Abort a pending post-create timer so it can't fire after logout.
      clearTimeout(state.successTimeout)
      state.successTimeout = null
      emit("tabChanged", "userLogin")
      store.dispatch("kiosks/userLogout")
    }

    const setCreatePassTab = (tab) => {
      // A successful New Pass create may have a pending post-success timer.
      // Switching tabs hides its success message, so clear the timer too;
      // otherwise it fires later and jumps to the active-pass screen (or
      // interrupts an SLP that was just submitted on the other tab).
      clearTimeout(state.successTimeout)
      state.successTimeout = null
      state.activeCreateTab = tab
      resetResponseMessages()
    }

    const focusTabByIndex = (index) => {
      nextTick(() => {
        const tabs = tabListRef.value
          ? tabListRef.value.querySelectorAll('[role="tab"]')
          : []
        if (tabs[index]) {
          tabs[index].focus()
        }
      })
    }

    // ARIA Tabs keyboard pattern: roving tabindex requires arrow-key navigation,
    // otherwise the inactive tab (tabindex="-1") is unreachable by keyboard.
    const onTabKeydown = (event) => {
      const navigationKeys = ["ArrowRight", "ArrowLeft", "Home", "End"]
      if (!navigationKeys.includes(event.key)) {
        return
      }
      event.preventDefault()
      const lastIndex = createPassTabOrder.length - 1
      const currentIndex = createPassTabOrder.indexOf(state.activeCreateTab)
      let nextIndex = currentIndex
      if (event.key === "ArrowRight") {
        nextIndex = currentIndex >= lastIndex ? 0 : currentIndex + 1
      } else if (event.key === "ArrowLeft") {
        nextIndex = currentIndex <= 0 ? lastIndex : currentIndex - 1
      } else if (event.key === "Home") {
        nextIndex = 0
      } else if (event.key === "End") {
        nextIndex = lastIndex
      }
      setCreatePassTab(createPassTabOrder[nextIndex])
      focusTabByIndex(nextIndex)
    }

    const onSlpTabChanged = (tab) => {
      emit("tabChanged", tab)
    }

    const onSlpCreated = (pass) => {
      emit("kioskSlpCreated", pass)
    }

    watch(
      () => isTardyCreateAllow.value,
      (newVal) => {
        if (!newVal.ltc && state.form.tardy_badge == "LTC") {
          state.form.tardy_badge = ""
        }
        if (!newVal.lts && state.form.tardy_badge == "LTS") {
          state.form.tardy_badge = ""
        }
      }
    )

    watch(isSlpTabVisible, (isVisible) => {
      if (!isVisible && state.activeCreateTab === createPassTabs.SLP) {
        state.activeCreateTab = createPassTabs.NEW_PASS
      }
    })

    const checkUnavailable = (value) => {
      state.form.to = value
      store
        .dispatch("kiosks/getUnavailability", {
          id: value.value.id,
          type: value.value.type
        })
        .then((response) => {
          if (response.data.data) {
            state.form.to.value.unavailability = response.data.data
          }
        })
    }

    return {
      state,
      createPassTabs,
      tabListRef,
      onTabKeydown,
      activeKiosk,
      access_token,
      activePass,
      isTardyCreateAllow,
      isSlpTabVisible,
      isPassLimitReached,
      passLimit,
      passLimitMax,
      passLimitAvailable,
      createNewPassData,
      createMode,
      fromObj,
      favorites,
      destinationUnavailability,
      createPass,
      cancel,
      setCreatePassTab,
      onSlpTabChanged,
      onSlpCreated,
      getFavorites,
      errors,
      checkUnavailable
    }
  }
}
</script>
